import { ImportFileResponse } from "@/common/utils/types";
import Papa from "papaparse";
import StatDisplay from "../StatDisplay";
import { useMemo } from "react";

interface ImportResultsProps {
    parsedCsv: Papa.ParseResult<unknown> | undefined;
    mappingResults: ImportFileResponse | null;
}

const ImportResults: React.FC<ImportResultsProps> = ({ parsedCsv, mappingResults }) => {
    // get the number of failed rows
    const numFailedRows = useMemo(
        () => (mappingResults ? new Set(mappingResults.rowFailures.map((row) => row.offset)).size : 0),
        [mappingResults],
    );

    // get the total number of rows
    const numTotalRows = parsedCsv?.data?.length || 0;

    // get the number of successfully imported rows
    const successfullyImportedRows = numTotalRows - numFailedRows;

    // render import row
    const renderImportRow = (rowNumber: number, patientName: string, index: number) => {
        return (
            <div key={index} className="flex gap-2 items-center py-1 w-full max-md:max-w-full">
                <div className="flex overflow-hidden flex-1 shrink gap-2 items-center self-stretch py-1 pr-2 pl-2.5 my-auto w-full bg-red-50 rounded-md basis-0  max-md:max-w-full">
                    <div className="flex flex-1 shrink gap-4 items-start self-stretch my-auto w-full basis-0 max-md:max-w-full">
                        <div className="w-[50px]">{rowNumber}</div>
                        <div>{patientName}</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 ">
            <div className="w-full flex flex-col gap-4 overflow-y-auto max-h-[300px] px-6">
                {/* subtitle */}
                <div className="text-sm text-gray-600">
                    There were <span className="font-semibold text-red-700">{numFailedRows} failed rows</span> of the{" "}
                    <span className="font-semibold">{numTotalRows} total rows imported.</span> Below is a list of
                    errors.
                </div>
                {/* stat cards */}
                <div className="w-full flex gap-4 justify-start items-center">
                    <StatDisplay
                        value={numFailedRows.toString()}
                        label="FAILED ROWS"
                        backgroundColor="bg-red-50"
                        width="w-1/4"
                    />
                    <StatDisplay
                        value={successfullyImportedRows.toString()}
                        label="SUCCESSFUL ROWS"
                        backgroundColor="bg-max-50"
                        width="w-1/4"
                    />
                </div>
                {/* table */}
                <div className="flex flex-col text-sm leading-5 text-gray-900 w-full">
                    <div className="flex gap-2 items-center py-0.5 w-full text-xs leading-snug whitespace-nowrap text-slate-600 max-md:max-w-full">
                        <div className="flex overflow-hidden flex-1 shrink gap-2 items-center self-stretch py-1 pr-2 pl-2.5 my-auto w-full rounded-md basis-0 min-w-[240px] max-md:max-w-full">
                            <div className="flex flex-wrap flex-1 shrink gap-5 items-start self-stretch my-auto w-full basis-0 max-md:max-w-full">
                                <div className="w-[50px]">ROW</div>
                                <div className="w-[50px]">FAILURE</div>
                            </div>
                        </div>
                    </div>
                    {mappingResults?.rowFailures.map((row, index) =>
                        renderImportRow(row.offset - 1, row.failure, index),
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportResults;
